"use client";

import { Autocomplete, Card, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import { useCallback, useMemo, useState } from "react";
import InputMaskWrapper from "@/components/atoms/InputMaskWrapper";
import { useNavigate } from "react-router-dom";

import CurrencyInput from "@/components/atoms/CurrencyInput";
import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useUnitDeliveryTime from "@/hooks/use-unit-delivery-time";
import { CoverageAreaFormMessages } from "@/messages/coverage-area/coverage-area-form.messages";

import { coverageAreaFormNames } from "./form-data";
import { CoverageAreaFormData } from "./types";
import useGetCoverageAreas from "@/hooks/requests/coverage-area/use-get-coverage-areas";
import { FilterType, IFilter } from "@/types/filter.types";
import ICoverageArea from "@/interfaces/coverage-area.interface";

type Props = {
  messages: CoverageAreaFormMessages;
  isLoading: boolean;
  isEditing?: boolean;
};

function CoverageAreaForm({ messages, isLoading }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const { regionalDeliveryTimeLabel } = useUnitDeliveryTime();
  const formik = useFormikContext<CoverageAreaFormData>();
  const { values, errors, submitForm, handleChange, setFieldValue } = formik;

  const emptyFilters = [
    { property: "areaDescription", value: "", filterType: FilterType.Contains },
  ] as IFilter<ICoverageArea>[];
  const { data: allAreasData } = useGetCoverageAreas({
    filters: emptyFilters,
    pageNumber: 0,
    pageSize: 1000,
  });

  const availableFreightTypes = useMemo(() => {
    const items = allAreasData?.items || [];
    const normalize = (s: string) => {
      const lower = s.trim().toLowerCase();
      if (!lower) return "";
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    };
    const set = new Set<string>();
    items.forEach((i) => {
      if (typeof i.freightType === "string" && i.freightType.trim()) {
        set.add(normalize(i.freightType));
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allAreasData]);
  const hasFreightTypeOptions = availableFreightTypes.length > 0;

  const formatWeight = useCallback((v: number | string) => {
    const n = typeof v === "string" ? parseFloat(v.replace(",", ".")) : Number(v);
    return isNaN(n) ? "0,000" : n.toFixed(3).replace(".", ",");
  }, []);

  const [startWeightText, setStartWeightText] = useState(formatWeight(values.startWeight));
  const [endWeightText, setEndWeightText] = useState(formatWeight(values.endWeight));

  const handleWeightBlur = (field: string, text: string, setText: (v: string) => void) => {
    const parsed = parseFloat(text.replace(",", "."));
    const value = isNaN(parsed) ? 0 : parseFloat(parsed.toFixed(3));
    setFieldValue(field, value);
    setText(formatWeight(value));
  };

  return (
    <MDBox>
      <Card>
        <MDBox p={3}>
          <MDTypography variant="h5">{messages.coverageAreaCardTitle}</MDTypography>
        </MDBox>

        <MDBox component="form" pb={3} px={3}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 3 }}>
              <InputMaskWrapper
                mask={"99999-999"}
                value={values.startZipCode}
                name={coverageAreaFormNames.startZipCode}
                onChange={handleChange}
                variant="standard"
                label={messages.cepStartLabel}
                fullWidth
                error={!!errors.startZipCode}
                helperText={errors.startZipCode}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <InputMaskWrapper
                mask={"99999-999"}
                value={values.endZipCode}
                name={coverageAreaFormNames.endZipCode}
                onChange={handleChange}
                variant="standard"
                label={messages.cepEndLabel}
                fullWidth
                error={!!errors.endZipCode}
                helperText={errors.endZipCode}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormField
                placeholder={messages.areaDescriptionPlaceholder}
                fullWidth
                variant="standard"
                label={messages.areaDescriptionLabel}
                name={coverageAreaFormNames.areaDescription}
                onChange={handleChange}
                value={values.areaDescription}
                error={!!errors.areaDescription}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 1.5 }}>
              <MDBox mb={1.5}>
                <TextField
                  fullWidth
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ inputMode: "decimal" }}
                  label={messages.startWeightLabel}
                  value={startWeightText}
                  onChange={(e) => setStartWeightText(e.target.value)}
                  onBlur={() =>
                    handleWeightBlur(
                      coverageAreaFormNames.startWeight,
                      startWeightText,
                      setStartWeightText
                    )
                  }
                  error={!!errors.startWeight}
                  helperText={errors.startWeight}
                  required
                />
              </MDBox>
            </Grid>

            <Grid size={{ xs: 12, md: 1.5 }}>
              <MDBox mb={1.5}>
                <TextField
                  fullWidth
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ inputMode: "decimal" }}
                  label={messages.endWeightLabel}
                  value={endWeightText}
                  onChange={(e) => setEndWeightText(e.target.value)}
                  onBlur={() =>
                    handleWeightBlur(
                      coverageAreaFormNames.endWeight,
                      endWeightText,
                      setEndWeightText
                    )
                  }
                  error={!!errors.endWeight}
                  helperText={errors.endWeight}
                  required
                />
              </MDBox>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <CurrencyInput
                name={coverageAreaFormNames.freightChargeOrder}
                label={messages.freightChargeOrderLabel}
                value={values.freightChargeOrder}
                onChange={handleChange}
                fullWidth
                error={!!errors.freightChargeOrder}
                helperText={errors.freightChargeOrder}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <CurrencyInput
                name={coverageAreaFormNames.freightCostCarrier}
                label={messages.freightCostCarrierLabel}
                value={values.freightCostCarrier}
                onChange={handleChange}
                fullWidth
                error={!!errors.freightCostCarrier}
                helperText={errors.freightCostCarrier}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormField
                type="number"
                fullWidth
                variant="standard"
                InputLabelProps={{ shrink: true }}
                label={regionalDeliveryTimeLabel}
                name={coverageAreaFormNames.regionalDeliveryTime}
                onChange={handleChange}
                value={values.regionalDeliveryTime}
                error={!!errors.regionalDeliveryTime}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              {hasFreightTypeOptions ? (
                <Autocomplete
                  freeSolo
                  options={availableFreightTypes}
                  value={values.freightType || ""}
                  onInputChange={(_, newInput) =>
                    setFieldValue(coverageAreaFormNames.freightType, newInput)
                  }
                  onChange={(_, newValue) =>
                    setFieldValue(coverageAreaFormNames.freightType, newValue || "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label={messages.freightTypeLabel}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  )}
                />
              ) : (
                <FormField
                  fullWidth
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  label={messages.freightTypeLabel}
                  name={coverageAreaFormNames.freightType}
                  onChange={handleChange}
                  value={values.freightType || ""}
                />
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <CurrencyInput
                name={coverageAreaFormNames.freeShippingOver}
                label={messages.freeShippingFromLabel}
                value={values.freeShippingOver ?? 0}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
        <MDButton
          variant="gradient"
          color="light"
          onClick={() => router(ROUTE_URLS.coverageArea(lang))}
          isLoading={isLoading}
        >
          {messages.cancelButton}
        </MDButton>

        <MDButton variant="gradient" color="dark" onClick={submitForm} isLoading={isLoading}>
          {messages.updateButton}
        </MDButton>
      </MDBox>
    </MDBox>
  );
}

export default CoverageAreaForm;
